import { useState, useEffect, useMemo } from 'react';
import { normalizeOwnerName } from '../utils/ownerNormalization';
import { useAppConfig } from './useAppConfig';
import { City, PropertyType, AppUser, UserRole, VisibilityMatrixValue } from '../types';
import { useConnection } from '../context/ConnectionContext';
import { fetchLocationsConfigApi, fetchInmobiliariasConfigApi } from '../services/configApiClient';

export interface OwnerOption {
  id: string;
  name: string;
  docId: string;
}

export const useBrokerFilterOptions = (
  appUser: AppUser | null,
  _selectedCities: string[],
  _selectedLocalidades: string[]
) => {
  const [filterDataSource, setFilterDataSource] = useState<any>(null);
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(true);
  const [availablePropertyTypes, setAvailablePropertyTypes] = useState<PropertyType[]>([]);
  const [availableOwners, setAvailableOwners] = useState<OwnerOption[]>([]);
  const [inmobiliariasConfig, setInmobiliariasConfig] = useState<Record<string, any>>({});
  const [allLocations, setAllLocations] = useState<City[]>([]);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);
  const { setIsOffline } = useConnection();
  const { appConfig } = useAppConfig();

  const visibilityMatrix = useMemo(() => {
    return (appConfig?.visibility_matrix?.value as VisibilityMatrixValue) || {};
  }, [appConfig]);

  const allowedOwnersForUser = useMemo(() => {
    if (!appUser || !appUser.role) return [];
    if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OPERACION].includes(appUser.role)) return null;
    const allowed = visibilityMatrix[appUser.role] || [];
    return allowed.map((o) => normalizeOwnerName(o));
  }, [appUser, visibilityMatrix]);

  // 1. Cargar ubicaciones y tipos desde el backend
  useEffect(() => {
    let isMounted = true;
    const loadLocations = async () => {
      try {
        setIsLoadingCities(true);
        const data = await fetchLocationsConfigApi(false, appUser?.role);
        if (!isMounted) return;

        setFilterDataSource(data);
        const propertyTypes = (data.tiposInmueble || [])
          .filter(Boolean)
          .map((name) => ({ id: String(name).toLowerCase(), name: String(name) }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setAvailablePropertyTypes(propertyTypes);

        const ubicacionesMap = data.ubicaciones || {};
        const cities = Object.keys(ubicacionesMap).map((name) => ({ id: name.toLowerCase(), name }));
        setAvailableCities(cities);

        const locations: City[] = [];
        Object.keys(ubicacionesMap).sort().forEach((city) => {
          locations.push({ id: city, name: city });
          const localitiesMap = ubicacionesMap[city] || {};
          Object.keys(localitiesMap).sort().forEach((loc) => {
            locations.push({ id: `${city}, ${loc}`, name: `${city}, ${loc}` });
            const neighborhoods = localitiesMap[loc];
            if (Array.isArray(neighborhoods)) {
              neighborhoods.sort().forEach((barrio) => {
                locations.push({ id: `${city}, ${loc}, ${barrio}`, name: `${city}, ${loc}, ${barrio}` });
              });
            }
          });
        });
        setAllLocations(locations);
        setIsLoadingCities(false);
        setIsOffline(false);
      } catch (error: any) {
        if (!isMounted) return;
        setFilterOptionsError(error?.message || 'Error cargando ubicaciones');
        setIsLoadingCities(false);
      }
    };

    loadLocations();
    return () => { isMounted = false; };
  }, [setIsOffline, appUser?.role]);

  // 2. Cargar propietarios e inmobiliarias desde el backend
  useEffect(() => {
    let isMounted = true;
    const loadOwners = async () => {
      if (!appUser) return;
      try {
        const configMap = await fetchInmobiliariasConfigApi();
        if (!isMounted) return;

        const ownersList: OwnerOption[] = [];
        Object.keys(configMap).forEach((docId) => {
          const data = configMap[docId];
          if (data && typeof data === 'object') {
            Object.keys(data).forEach((key) => {
              if (key !== 'id' && typeof data[key] === 'object') {
                const normalizedKey = normalizeOwnerName(key);
                if (allowedOwnersForUser === null || allowedOwnersForUser.includes(normalizedKey)) {
                  ownersList.push({ id: key, name: key, docId });
                }
              }
            });
          }
        });

        setAvailableOwners(ownersList.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))));
        setInmobiliariasConfig(configMap);
      } catch (error) {
        console.error('Error fetching owners from API:', error);
      }
    };

    loadOwners();
    return () => { isMounted = false; };
  }, [appUser, allowedOwnersForUser]);

  return {
    availableCities,
    isLoadingCities,
    availablePropertyTypes,
    availableOwners,
    inmobiliariasConfig,
    allLocations,
    filterOptionsError,
    filterDataSource,
  };
};
